import { ComponentType } from 'react'

import { BonusSvg } from './iport/color/icons/bonus-svg'
import { CheckCircleSvg } from './iport/color/icons/check-circle-svg'
import { ClockSvg } from './iport/color/icons/clock-svg'
import { DiscountSvg } from './iport/color/icons/discount-svg'
import { EducationSvg } from './iport/color/icons/education-svg'
import { ErrorCircleSvg } from './iport/color/icons/error-circle-svg'
import { FailedOrderSvg } from './iport/color/icons/failed-order-svg'
import { LoveSvg } from './iport/color/icons/love-svg'
import { NetworkErrorFillSvg } from './iport/color/icons/network-error-fill-svg'
import { PolicySvg } from './iport/color/icons/policy-svg'
import { ServiceSvg } from './iport/color/icons/service-svg'
import { SettingSvg } from './iport/color/icons/setting-svg'
import { ToolSvg } from './iport/color/icons/tool-svg'
import { TradeInSvg } from './iport/color/icons/trade-in-svg'
import { CheckCircleSvg as CheckCircleMonochromeSvg } from './iport/monochrome/icons/check-circle-svg'
import { DocumentSvg as DocumentMonochromeSvg } from './iport/monochrome/icons/document-svg'
import { ErrorCircleSvg as ErrorCircleMonochromeSvg } from './iport/monochrome/icons/error-circle-svg'
import { ArrowDownSvg } from './system/icons/arrow-down-svg'
import { ArrowLeftSvg } from './system/icons/arrow-left-svg'
import { ArrowRightSvg } from './system/icons/arrow-right-svg'
import { ArrowUpSvg } from './system/icons/arrow-up-svg'
import { ChartSvg } from './system/icons/chart-svg'
import { CloseSvg } from './system/icons/close-svg'
import { SearchSvg } from './system/icons/search-svg'
import { WarningCircleSvg } from './system/icons/warning-circle-svg'
import { IconProps } from './types/types'

/**
 * Icons are resolved through these maps instead of a namespace object, so the bundler can drop
 * every icon that is not listed here. Using an icon on a page means adding one import and one
 * entry below — an unregistered name fails to compile, since DesignIcon is derived from the keys.
 */
export const SYSTEM_ICONS = {
	ArrowDown: ArrowDownSvg,
	ArrowLeft: ArrowLeftSvg,
	ArrowRight: ArrowRightSvg,
	ArrowUp: ArrowUpSvg,
	Chart: ChartSvg,
	Close: CloseSvg,
	Search: SearchSvg,
	WarningCircle: WarningCircleSvg
} as const satisfies Record<string, ComponentType<IconProps>>

export const IPORT_ICONS = {
	IPortBonus: BonusSvg,
	IPortCheckCircle: CheckCircleSvg,
	IPortClock: ClockSvg,
	IPortDiscount: DiscountSvg,
	IPortEducation: EducationSvg,
	IPortErrorCircle: ErrorCircleSvg,
	IPortFailedOrder: FailedOrderSvg,
	IPortLove: LoveSvg,
	IPortNetworkErrorFill: NetworkErrorFillSvg,
	IPortPolicy: PolicySvg,
	IPortService: ServiceSvg,
	IPortSetting: SettingSvg,
	IPortTool: ToolSvg,
	IPortTradeIn: TradeInSvg
} as const satisfies Record<string, ComponentType<IconProps>>

export const IPORT_MONOCHROME_ICONS = {
	IPortCheckCircleMonochrome: CheckCircleMonochromeSvg,
	IPortDocumentMonochrome: DocumentMonochromeSvg,
	IPortErrorCircleMonochrome: ErrorCircleMonochromeSvg
} as const satisfies Record<string, ComponentType<IconProps>>

export const ICONS = {
	...SYSTEM_ICONS,
	...IPORT_ICONS,
	...IPORT_MONOCHROME_ICONS
} as const

export type SystemIcon = keyof typeof SYSTEM_ICONS
export type IPortIcon = keyof typeof IPORT_ICONS
export type IPortIconMonochrome = keyof typeof IPORT_MONOCHROME_ICONS
export type DesignIcon = keyof typeof ICONS

/**
 * A key present in two sets would be silently shadowed by the spread above (last one wins) while
 * isSystemIcon() still reported the old set, so keep the three sets disjoint. TypeScript does not
 * flag duplicate keys introduced via spread, hence this assertion.
 */
type AssertDisjoint<A extends string, B extends string> = Extract<A, B> extends never ? true : never
const _iconSetsAreDisjoint: AssertDisjoint<SystemIcon, IPortIcon | IPortIconMonochrome> &
	AssertDisjoint<IPortIcon, IPortIconMonochrome> = true
void _iconSetsAreDisjoint
